import React,{ useState, useEffect, useMemo, useRef } from "react";
import { useLocation, NavLink } from "react-router-dom";
import {
  ExpandLess,
  ExpandMore,
  CheckCircle as SubscribedIcon,
  Cancel as NotSubscribedIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Button,
  Tooltip,
  useTheme,
  useMediaQuery,
  Avatar,
} from "@mui/material";
import TeamInboxFilled from "../shared/icons/teaminbox_filled.svg";
import TeamInboxOutlined from "../shared/icons/teaminbox_outlined.svg";
import CallFilled from "../shared/icons/call_filled.svg";
import CallOutlined from "../shared/icons/call_outlined.svg";
import ContactFilled from "../shared/icons/contact_filled.svg";
import ContactOutlined from "../shared/icons/contact_outlined.svg";
import Broadcast from "../shared/icons/broadcast1.svg";
import Broadcast1 from "../shared/icons/broadcast.svg";
import Tag from "../shared/icons/tag_outlined.svg";
import TagFilled from "../shared/icons/tag_filled.svg";
import Automation from "../shared/icons/automation_filled.svg";
import Automation1 from "../shared/icons/automation_outlined.svg";
import Home1  from "../shared/icons/home_outlined.svg";
import Home  from "../shared/icons/home_filled.svg";
import AppointmentOutlined from  "../shared/icons/appointment.svg";
import AppointmentFilled from  "../shared/icons/appointment1.svg";
import Ticket from "../shared/icons/ticket_filled.svg";
import Ticket1 from "../shared/icons/ticket_outlined.svg";
import Plan from "../shared/icons/plan_filled.svg"
import Plan1 from "../shared/icons/plan_outlined.svg"
import Setting from "../shared/icons/setting_filled.svg"
import Setting1 from "../shared/icons/setting_outlined.svg"
import Person from "../shared/icons/person_filled.svg";
import Person1 from "../shared/icons/person_outlined.svg";
import Lead from "../shared/icons/lead_filled.svg";
import Lead1 from "../shared/icons/lead_outlined.svg";
import Chat from "../shared/icons/chat_filled.svg";
import Chat1 from "../shared/icons/chat_outlined.svg";
import Logo from "../assets/logo.png";
import IconLogo from "../assets/smalllogo.svg";
import ServicesService from "../services/ServicesService";

const SIDEBAR_WIDTH = 248;
const COLLAPSED_WIDTH = 72;

const ESTORE_MERCHANT_URL = process.env.REACT_APP_ESTORE_MERCHANT_URL
  ? process.env.REACT_APP_ESTORE_MERCHANT_URL
  : "http://app.localhost:8080/account";

const navSections = [
  {
    label: "Messaging",
    defaultOpen: true,
    items: [
      { id: "dashboard", label: "Home", path: "/dashboard",activeIcon:Home, inactiveIcon:Home1 },
      { id: "teamchat", label: "Team Inbox", path: "/teamchat", activeIcon: TeamInboxFilled, inactiveIcon: TeamInboxOutlined },
      { id: "calling", label: "Calling", path: "/calling",activeIcon:CallFilled, inactiveIcon:CallOutlined  },
      { id: "chat-simulation", label: "Chat Simulation", path: "/chat-simulation", activeIcon: Chat, inactiveIcon: Chat1 },
      {
        id: "contacts",
        label: "Contacts",
        activeIcon: ContactFilled,
        inactiveIcon: ContactOutlined,
        children: [
          { id: "contacts-list", label: "Contacts", path: "/contacts" },
          { id: "group", label: "Groups", path: "/group" },
        ],
      },
      { id: "tags", label: "Tags", path: "/tags", activeIcon: TagFilled, inactiveIcon:  Tag },
    ],
  },
  {
    label: "CRM",
    defaultOpen: true,
    items: [
      {
        id: "leads",
        label: "Leads",
        activeIcon:Lead,
        inactiveIcon:Lead1,
        children: [
          { id: "leads-board", label: "Pipeline", path: "/leads" },
          { id: "leads-list", label: "All Leads", path: "/leads/list" },
          { id: "leads-stages", label: "Pipeline Stages", path: "/leads/pipeline-stages" },
          { id: "leads-categories", label: "Categories", path: "/leads/categories" },
          { id: "quotations", label: "Quotations", path: "/quotations" },
        ],
      },
    ],
  },
  {
    label: "Campaigns",
    defaultOpen: true,
    items: [
      {
        id: "broadcast",
        label: "Broadcast",
        activeIcon: Broadcast,
        inactiveIcon: Broadcast1,
        children: [
          { id: "templates", label: "Templates", path: "/broadcast/templates" },
          { id: "broadcast-messages", label: "Broadcast Messages", path: "/broadcast/messages" },
        ],
      },
      {
        id: "automation",
        label: "Automation",
        activeIcon: Automation,
        inactiveIcon: Automation1,
        children: [
          { id: "automation-rules", label: "Rules", path: "/automation/rules" },
          { id: "automation-keyword-actions", label: "Keyword Actions", path: "/automation/keyword-actions" },
          { id: "automation-chatbots", label: "Chatbots", path: "/automation/chatbots" },
          { id: "automation-forms", label: "Forms", path: "/automation/forms" },
          { id: "automation-ai-chatbot", label: "AI Chatbot", path: "/ai-chatbot" },
        ],
      },
    ],
  },
  {
    label: "Operations",
    defaultOpen: true,
    items: [
      { id: "appointments", label: "Appointments", path: "/appointments", activeIcon: AppointmentFilled, inactiveIcon: AppointmentOutlined },
      { id: "tickets", label: "Tickets & Reservations", path: "/tickets", activeIcon: Ticket, inactiveIcon:Ticket1},
    ],
  },
  {
    label: "Account",
    defaultOpen: true,
    items: [
      { id: "plan", label: "Plans", path: "/plan", activeIcon: Plan, inactiveIcon:Plan1},
      { id: "settings", label: "Settings", path: "/settings", activeIcon:Setting, inactiveIcon:Setting1},
      { id: "team-members", label: "Team Members", path: "/team-members", activeIcon:Person ,inactiveIcon:Person1},
    ],
  },
];

function filterSectionsByBusinessSetup(sections, businessSetup) {
  const goals = Array.isArray(businessSetup?.goals) ? businessSetup.goals : [];
  const shouldFilterOperations = businessSetup?.completed && goals.length > 0;
  if (!shouldFilterOperations) return sections;

  return sections
    .map((section) => {
      if (section.label !== "Operations") return section;
      const items = section.items.filter((item) => {
        if (item.id === "appointments") return goals.includes("appointments");
        if (item.id === "tickets") return goals.includes("tickets");
        return true;
      });
      return { ...section, items };
    })
    .filter((section) => section.items.length > 0);
}

function isPathActive(pathname, item) {
  if (item.path) {
    return pathname === item.path || pathname.startsWith(item.path + "/");
  }
  if (item.children) {
    return item.children.some((child) => pathname === child.path || pathname.startsWith(child.path + "/"));
  }
  return false;
}

function NavItem({ item, pathname, isSubItem }) {
  const isActive = isPathActive(pathname, item);
  const iconSrc = item.activeIcon && item.inactiveIcon ? (isActive ? item.activeIcon : item.inactiveIcon) : null;
  const Icon = item.Icon;

  return (
    <ListItemButton
      component={NavLink}
      to={item.path}
      selected={isActive}
      sx={{
        pl: isSubItem ? 6 : 3,
        py: 1,
        ...(isSubItem
          ? {
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 37,
                top: "50%",
                width: 12,
                borderTop: "1.5px solid rgba(137, 159, 185, 0.55)",
                transform: "translateY(-50%)",
              },
            }
          : {}),
        "&.Mui-selected": {
          backgroundColor: "var(--theme-sidebar-active-bg)",
          color: "var(--theme-primary)",
          borderRight: "3px solid var(--theme-primary)",
          "&:hover": { backgroundColor: "var(--theme-sidebar-active-bg)" },
        },
        "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
      }}
    >
      {iconSrc ? (
        <ListItemIcon sx={{ minWidth: 36 }}>
          <img src={iconSrc} alt="" style={{ width: 20, height: 20 }} />
        </ListItemIcon>
      ) : Icon ? (
        <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
          <Icon fontSize="small" />
        </ListItemIcon>
      ) : null}
      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14,noWrap: true, }} />
    </ListItemButton>
  );
}

function NavItemWithSubmenu({ item, pathname, open, onToggle }) {
  const isParentActive = isPathActive(pathname, item);
  const iconSrc = item.activeIcon && item.inactiveIcon ? (isParentActive ? item.activeIcon : item.inactiveIcon) : null;

  return (
    <Box>
      <ListItemButton
        onClick={onToggle}
        selected={isParentActive}
        sx={{
          pl: 3,
          py: 1,
          "&.Mui-selected": {
            backgroundColor: "var(--theme-sidebar-active-bg)",
            color: "var(--theme-primary)",
            "&:hover": { backgroundColor: "var(--theme-sidebar-active-bg)" },
          },
          "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
        }}
      >
        {iconSrc ? (
          <ListItemIcon sx={{ minWidth: 36 }}>
            <img src={iconSrc} alt="" style={{ width: 20, height: 20 }} />
          </ListItemIcon>
        ) : null}
        <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
        {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          disablePadding
          sx={{
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 37,
              top: 2,
              bottom: 2,
              borderLeft: "1.5px solid rgba(137, 159, 185, 0.45)",
            },
          }}
        >
          {item.children.map((child) => (
            <NavItem key={child.id} item={child} pathname={pathname} isSubItem />
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

export default function AppSidebar({
  mobileOpen,
  onClose,
  desktopCollapsed = false,
  account,
  onOpenProfile,
  onLogout,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const pathname = location.pathname;
  // const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [businessSetup, setBusinessSetup] = useState(null);
  // const hoverTimerRef = useRef(null);
  const visibleNavSections = useMemo(
    () => filterSectionsByBusinessSetup(navSections, businessSetup),
    [businessSetup]
  );

  const [openSections, setOpenSections] = useState(
    navSections.reduce((acc, s) => ({ ...acc, [s.label]: s.defaultOpen }), {})
  );

  const [openNavItems, setOpenNavItems] = useState(() => {
    const initial = {};
    navSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children) {
          initial[item.id] = item.children.some(
            (c) => pathname === c.path || pathname.startsWith(c.path + "/")
          );
        }
      });
    });
    return initial;
  });

  useEffect(() => {
    let mounted = true;
    const loadBusinessSetup = () => {
      ServicesService.getServiceSettings()
        .then((settings) => {
          if (!mounted) return;
          setBusinessSetup(settings?.allSettings?.business_setup || null);
        })
        .catch(() => {
          if (mounted) setBusinessSetup(null);
        });
    };
    loadBusinessSetup();
    window.addEventListener("business-setup-updated", loadBusinessSetup);
    return () => {
      mounted = false;
      window.removeEventListener("business-setup-updated", loadBusinessSetup);
    };
  }, []);

  useEffect(() => {
    setOpenNavItems((prev) => {
      let next = { ...prev };
      let changed = false;
      visibleNavSections.forEach((section) => {
        section.items.forEach((item) => {
          if (item.children) {
            const shouldBeOpen = item.children.some(
              (c) => pathname === c.path || pathname.startsWith(c.path + "/")
            );
            if (shouldBeOpen && !prev[item.id]) {
              next[item.id] = true;
              changed = true;
            }
          }
        });
      });
      return changed ? next : prev;
    });
  }, [pathname, visibleNavSections]);

  const toggleSection = (label) => {
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const toggleNavItem = (id) => {
    setOpenNavItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const subscription = account?.getSubscription?.();
  const isSubscribed = subscription && subscription.getStatus() === "active";

  const handleOpenProfile = () => {
    onOpenProfile?.();
    if (isMobile) onClose?.();
  };

  const isDesktopExpanded = !isMobile; 
  const isSidebarCollapsed = false; 
 

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "var(--theme-sidebar-bg)",
        color: "var(--theme-sidebar-text)",
        position: "relative",
      }}
    >
      <Box
        sx={{
          p: 2, 
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minHeight: 62,
        }}
      >
        <Box
          component="img"
          src={Logo}
          alt="Logo"
          sx={{
            height: 46,
            width: 128,
            objectFit: "contain",
            display: "block",
            objectPosition: "left center",
          }}
        />
      </Box>

      
      <List component="nav" sx={{ flex: 1, overflow: "auto", py: 1, px: 0 }}>
        {visibleNavSections.map((section) => (
          <Box key={section.label}>
            <ListItemButton
              onClick={() => toggleSection(section.label)}
              sx={{
                py: 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
              }}
            >
              <ListItemText
                primary={section.label}
                primaryTypographyProps={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--theme-sidebar-text-muted)" }}
              />
              {openSections[section.label] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </ListItemButton>
            <Collapse in={openSections[section.label]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {section.items.map((item) =>
                  item.children ? (
                    <NavItemWithSubmenu
                      key={item.id}
                      item={item}
                      pathname={pathname}
                      open={!!openNavItems[item.id]}
                      onToggle={() => toggleNavItem(item.id)}
                    />
                  ) : (
                    <NavItem key={item.id} item={item} pathname={pathname} />
                  )
                )}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.06)",
          background: "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.18) 100%)",
        }}
      >
        <Box
          onClick={handleOpenProfile}
          role="button"
          tabIndex={0}
          aria-label="Open profile"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleOpenProfile();
            }
          }}
          sx={{
            display: "flex",
            alignItems: "flex-start", 
            gap: 1.25,
            p: 1,
            mb:1,
            borderRadius: 1.8,
            border: "1px solid rgba(255,255,255,0.25)",
            cursor: "pointer",
            "&:hover": {
              borderColor: "rgba(255,255,255,0.4)",
              backgroundColor: "rgba(255,255,255,0.06)",
            },
          }}
        >
          <Avatar
            sx={{
              width: 35,
              height: 35,
              bgcolor: "var(--theme-primary)",
              flexShrink: 0,
            }}
          >
            <PersonIcon sx={{ fontSize: 25, color: "#fff" }} />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {account?.getFirstName?.() || "Account"}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: "var(--theme-sidebar-text-muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                mt: 0.25,
              }}
            >
              {account?.getEmail?.()|| "usesr@gmail.com" }
            </Typography>
            <Box
          sx={{
            borderRadius: 1,
            display: "inline-flex",
            px: 0.5,  
            alignItems: "center",                         
            py: 0.10,   
            mt: 0.5,
            gap: 0.75,
            width: "fit-content",  
            border: "1px solid var(--theme-sidebar-border, rgba(168, 166, 166, 0.1))",
            backgroundColor: "var(--theme-sidebar-surface, rgba(255,255,255,0.04))",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5,mt: 0.75,p:0, }}>
            {isSubscribed ? (
              <>
                {/* <SubscribedIcon sx={{ fontSize: 18, color: "var(--theme-success, #66bb6a)" }} /> */}
                <Typography  sx={{ fontSize: 12, }}>
                  Subscribed
                </Typography>
                {/* {subscription.getPlanName() && (
                  <Typography variant="caption" sx={{ color: "var(--theme-sidebar-text-muted)" }}>
                    ({subscription.getPlanName()})
                  </Typography>
                )} */}
              </>
            ) : (
              <>
                {/* <NotSubscribedIcon sx={{ fontSize: 18, color: "var(--theme-sidebar-text-muted)" }} /> */}
                <Typography sx={{ 
                  fontSize: 12,
                  whiteSpace: "nowrap", 
                  overflow: "hidden",
                  maxWidth: "100%",
                }}
                >
                 Not subscribed
                </Typography>
   
              </>
            )}
          </Box>
        </Box>
          </Box>
        </Box>
        
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            window.open(ESTORE_MERCHANT_URL, '_blank', 'noopener,noreferrer');
          }}
          sx={{
            mt: 0.3,
            mb: 1,
            justifyContent: "flex-start",
            textTransform: "none",
            borderRadius: 1.6,
            px: 1.2,
            py: 0.9,
            whiteSpace: "nowrap",      
            overflow: "hidden",           
            minWidth: 0,   
            color: "var(--theme-sidebar-text-muted)",
            border: "1px solid rgba(255,255,255,0.25)",
            "&:hover": {
              borderColor: "rgba(255,255,255,0.4)",
              backgroundColor: "rgba(255,255,255,0.06)",
            },
          }}
        >
          Estore Merchant
        </Button>
        <Button
          fullWidth
          variant="text"
          startIcon={<LogoutIcon fontSize="small" />}
          onClick={onLogout}
          sx={{
            mt: 0.3,
            justifyContent: "flex-start",
            textTransform: "none",
            borderRadius: 1.6,
            px: 1.2,
            py: 0.9,
            color: "var(--theme-sidebar-text-muted)",
            border: "1px solid transparent",
            "&:hover": {
              color: "var(--theme-danger, #f44336)",
              backgroundColor: "var(--theme-danger-soft, rgba(244,67,54,0.08))",
              borderColor: "rgba(244,67,54,0.25)",
            },
          }}
        >
          Logout
        </Button>
        
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            top: 0,
            left: 0,
            zIndex: theme.zIndex.drawer + 2,
            borderRight: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.easeInOut,
          duration: 260,
        }),
        "& .MuiDrawer-paper": {
          width: SIDEBAR_WIDTH,
          boxSizing: "border-box",
          top: 0,
          left: 0,
          zIndex: theme.zIndex.drawer + 6,
          overflowX: "hidden",
          borderRight: "1px solid rgba(255,255,255,0.08)",
          mt: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.easeInOut,
            duration: 260,
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}